import React from 'react';
import {
    Box,
    Paper,
    Typography,
    Stack,
    Button
} from '@mui/material';
import { Print as PrintIcon, FileDownload as DownloadIcon, SaveAlt as FileIcon } from '@mui/icons-material';
import { formatCurrency } from '../../../utils/compteClientEngine';
import { jsonToXlsx } from '../../../settings/themeSettings';
import { downloadSageFile } from '../../../utils/sageAccountingEngine';

const BordereauValidationViewer = ({
    transferRecord = null,
    onDownloadSageFile = null
}) => {
    if (!transferRecord) return null;

    const {
        numeroTransfert = 'TRF-2026-09-001',
        numeroSimulation = 'SIM-2026-09-001',
        numeroSelection = 'SEL-2026-09-001',
        journalPeriodStr = 'FAC — Septembre 2026 (du 01/09 au 30/09/2026)',
        dateValidation = new Date().toISOString(),
        user = 'Comptable AFT',
        nomFichierSage = 'TM_BASCULE_20260904.txt',
        repertoireDestination = 'C:\\Users\\HP\\Desktop',
        formatCodage = 'Texte délimité (;) — UTF-8',
        codeDossierSage = 'AFRICA FOR TOURISM',
        factures = [],
        nombreLignesEcriture = 0,
        totalDebit = 0,
        totalCredit = 0,
        fileContent = ''
    } = transferRecord;

    const dateTirage = new Date().toLocaleDateString('fr-FR') + ' à ' + new Date().toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
    const dateValStr = new Date(dateValidation).toLocaleDateString('fr-FR') + ' à ' + new Date(dateValidation).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
    const totalTTC = factures.reduce((s, f) => s + Number(f.montantTTC || 0), 0);

    const handleExportExcel = () => {
        const exportData = factures.map(f => ({
            'N° Facture': f.numeroFacture,
            'Date': f.dateFacture,
            'Client': f.clientNom,
            'Compte Tiers': f.clientCodeTiers,
            'N° Dossier / PNR': f.refDossier,
            'Nature': f.nature,
            'Montant TTC (CFA)': f.montantTTC,
            'Statut': 'Transférée',
            'Réf. Transfert': numeroTransfert
        }));
        jsonToXlsx(exportData, `Bordereau_Validation_${numeroTransfert}`);
    };

    const handlePrint = () => {
        window.print();
    };

    const handleDownloadSage = () => {
        if (onDownloadSageFile) {
            onDownloadSageFile();
        } else if (fileContent) {
            downloadSageFile(fileContent, nomFichierSage);
        }
    };

    const handleDownloadSageCsv = () => {
        if (fileContent) {
            const csvName = nomFichierSage.replace(/\.txt$/i, '.csv');
            downloadSageFile(fileContent, csvName);
        }
    };

    return (
        <Box sx={{ outline: 'none' }}>
            {/* TOOLBAR */}
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }} className="no-print" flexWrap="wrap" gap={1}>
                <Typography variant="body2" color="text.secondary">
                    Bordereau officiel archivé — Transfert N° {numeroTransfert}
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap">
                    <Button
                        size="small"
                        variant="contained"
                        color="primary"
                        startIcon={<FileIcon />}
                        onClick={handleDownloadSage}
                        sx={{ textTransform: 'none', fontWeight: 600 }}
                    >
                        Télécharger SAGE (.txt)
                    </Button>
                    <Button
                        size="small"
                        variant="outlined"
                        color="primary"
                        startIcon={<FileIcon />}
                        onClick={handleDownloadSageCsv}
                        sx={{ textTransform: 'none', fontWeight: 600 }}
                    >
                        Télécharger (.csv)
                    </Button>
                    <Button
                        size="small"
                        variant="outlined"
                        startIcon={<PrintIcon />}
                        onClick={handlePrint}
                        sx={{ textTransform: 'none', fontWeight: 600 }}
                    >
                        Imprimer le bordereau
                    </Button>
                    <Button
                        size="small"
                        variant="contained"
                        color="success"
                        startIcon={<DownloadIcon />}
                        onClick={handleExportExcel}
                        sx={{ textTransform: 'none', fontWeight: 600 }}
                    >
                        Export Excel
                    </Button>
                </Stack>
            </Stack>

            {/* DOCUMENT OFFICIEL IMPRIMABLE (PAGE 13 NOTE SAGE) */}
            <Paper
                elevation={0}
                className="printable-document"
                sx={{
                    p: 3,
                    border: '2px solid #0f172a',
                    borderRadius: '8px',
                    bgcolor: 'white',
                    maxWidth: '1150px',
                    mx: 'auto'
                }}
            >
                {/* EN-TÊTE TRANSVERSAL */}
                <Box sx={{ borderBottom: '2px solid #0f172a', pb: 1.5, mb: 2 }}>
                    <Box sx={{ display: 'grid', gridTemplateColumns: '1.2fr 2fr 1.2fr', alignItems: 'center' }}>
                        <Box>
                            <Typography variant="body1" fontWeight="bold" sx={{ color: '#0f172a' }}>
                                AFRICA FOR TOURISM SARL
                            </Typography>
                            <Typography variant="caption" color="text.secondary" display="block">
                                Agence de Voyages & Tourisme
                            </Typography>
                            <Typography variant="caption" color="text.secondary" display="block">
                                TravelManager v2.0
                            </Typography>
                        </Box>

                        <Box textAlign="center">
                            <Typography variant="h6" fontWeight="bold" sx={{ textTransform: 'uppercase', letterSpacing: 0.5, color: '#0f172a' }}>
                                Bordereau de validation du transfert comptable
                            </Typography>
                        </Box>

                        <Box textAlign="right">
                            <Typography variant="caption" display="block" color="text.secondary">
                                Date de tirage : {dateTirage}
                            </Typography>
                            <Typography variant="caption" display="block" color="text.secondary">
                                Page : 1 / 1
                            </Typography>
                        </Box>
                    </Box>

                    {/* GRILLE DES MÉTADONNÉES */}
                    <Box sx={{ mt: 2, p: 1.5, bgcolor: '#f8fafc', border: '1px solid #cbd5e1', borderRadius: '4px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                        <Box>
                            <Typography variant="caption" display="block">
                                <strong>N° de transfert :</strong> {numeroTransfert}
                            </Typography>
                            <Typography variant="caption" display="block">
                                <strong>N° de simulation d'origine :</strong> {numeroSimulation}
                            </Typography>
                            <Typography variant="caption" display="block">
                                <strong>Journal / Période :</strong> {journalPeriodStr}
                            </Typography>
                            <Typography variant="caption" display="block">
                                <strong>Validé le :</strong> {dateValStr}
                            </Typography>
                        </Box>
                        <Box>
                            <Typography variant="caption" display="block" sx={{ color: '#16a34a', fontWeight: 'bold' }}>
                                <strong>Statut :</strong> TRANSFÉRÉ — VALIDÉ
                            </Typography>
                            <Typography variant="caption" display="block">
                                <strong>N° de sélection d'origine :</strong> {numeroSelection}
                            </Typography>
                            <Typography variant="caption" display="block">
                                <strong>Dossier comptable SAGE :</strong> {codeDossierSage}
                            </Typography>
                            <Typography variant="caption" display="block">
                                <strong>Préparé par :</strong> {user}
                            </Typography>
                        </Box>
                    </Box>
                </Box>

                {/* BLOC FICHIER D'INTERFACE SAGE GÉNÉRÉ */}
                <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1, color: '#0f172a' }}>
                    Fichier d'interface SAGE généré
                </Typography>
                <Box sx={{ p: 1.5, mb: 2.5, bgcolor: '#f1f5f9', border: '1px solid #cbd5e1', borderRadius: '4px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
                    <Box>
                        <Typography variant="caption" display="block">
                            <strong>Nom du fichier :</strong> {nomFichierSage}
                        </Typography>
                        <Typography variant="caption" display="block">
                            <strong>Répertoire de destination :</strong> {repertoireDestination}
                        </Typography>
                    </Box>
                    <Box>
                        <Typography variant="caption" display="block">
                            <strong>Format / codage :</strong> {formatCodage}
                        </Typography>
                        <Typography variant="caption" display="block">
                            <strong>Nombre de lignes du fichier :</strong> {nombreLignesEcriture || (factures.length * 3)}
                        </Typography>
                    </Box>
                </Box>

                {/* RÉCAPITULATIF DU TRANSFERT */}
                <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1, color: '#0f172a' }}>
                    Récapitulatif du transfert
                </Typography>
                <Box sx={{ border: '1px solid #0f172a', borderRadius: '4px', overflow: 'hidden', mb: 3 }}>
                    <Box sx={{ display: 'grid', gridTemplateColumns: '160px 140px 180px 180px 1fr', bgcolor: '#0f172a', color: 'white', p: 1, fontWeight: 'bold', fontSize: '0.78rem' }}>
                        <Box sx={{ textAlign: 'center' }}>Nombre de factures</Box>
                        <Box sx={{ textAlign: 'center' }}>Lignes d'écriture</Box>
                        <Box sx={{ textAlign: 'right' }}>Total Débit (FCFA)</Box>
                        <Box sx={{ textAlign: 'right' }}>Total Crédit (FCFA)</Box>
                        <Box sx={{ textAlign: 'center' }}>Équilibre</Box>
                    </Box>
                    <Box sx={{ display: 'grid', gridTemplateColumns: '160px 140px 180px 180px 1fr', p: 1, alignItems: 'center', fontSize: '0.85rem', bgcolor: '#f8fafc', fontWeight: 'bold' }}>
                        <Box sx={{ textAlign: 'center' }}>{factures.length}</Box>
                        <Box sx={{ textAlign: 'center' }}>{nombreLignesEcriture || (factures.length * 3)}</Box>
                        <Box sx={{ textAlign: 'right' }}>{formatCurrency(totalDebit || totalTTC)}</Box>
                        <Box sx={{ textAlign: 'right' }}>{formatCurrency(totalCredit || totalTTC)}</Box>
                        <Box sx={{ textAlign: 'center', color: '#16a34a' }}>ÉQUILIBRÉ</Box>
                    </Box>
                </Box>

                {/* FACTURES MARQUÉES TRANSFÉRÉES */}
                <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1, color: '#0f172a' }}>
                    Factures marquées « Transférée »
                </Typography>
                <Box sx={{ border: '1px solid #0f172a', borderRadius: '4px', overflow: 'hidden', mb: 3 }}>
                    <Box sx={{ display: 'grid', gridTemplateColumns: '130px 90px 1.5fr 110px 120px 100px 120px 100px 140px', bgcolor: '#0f172a', color: 'white', p: 1, fontWeight: 'bold', fontSize: '0.75rem' }}>
                        <Box>N° facture</Box>
                        <Box>Date</Box>
                        <Box>Client</Box>
                        <Box>Compte tiers</Box>
                        <Box>N° dossier / PNR</Box>
                        <Box>Nature</Box>
                        <Box sx={{ textAlign: 'right' }}>Montant TTC</Box>
                        <Box sx={{ textAlign: 'center' }}>Statut</Box>
                        <Box>Réf. transfert</Box>
                    </Box>

                    {factures.map((f, idx) => (
                        <Box
                            key={f.id || idx}
                            sx={{
                                display: 'grid',
                                gridTemplateColumns: '130px 90px 1.5fr 110px 120px 100px 120px 100px 140px',
                                p: 0.8,
                                alignItems: 'center',
                                fontSize: '0.78rem',
                                borderBottom: '1px solid #e2e8f0',
                                bgcolor: idx % 2 === 0 ? 'white' : '#f8fafc'
                            }}
                        >
                            <Box sx={{ fontWeight: 600 }}>{f.numeroFacture}</Box>
                            <Box sx={{ color: 'text.secondary' }}>{f.dateFacture}</Box>
                            <Box sx={{ fontWeight: 600 }}>{f.clientNom}</Box>
                            <Box sx={{ color: '#2563eb', fontWeight: 600 }}>{f.clientCodeTiers}</Box>
                            <Box sx={{ color: 'text.secondary' }}>{f.refDossier}</Box>
                            <Box>{f.nature}</Box>
                            <Box sx={{ textAlign: 'right', fontWeight: 700 }}>{formatCurrency(f.montantTTC)}</Box>
                            <Box sx={{ textAlign: 'center', color: '#16a34a', fontWeight: 'bold' }}>Transférée</Box>
                            <Box sx={{ color: '#0f172a', fontWeight: 600 }}>{numeroTransfert}</Box>
                        </Box>
                    ))}

                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: '130px 90px 1.5fr 110px 120px 100px 120px 100px 140px',
                            bgcolor: '#f1f5f9',
                            p: 1.1,
                            fontWeight: 'bold',
                            fontSize: '0.82rem',
                            borderTop: '2px solid #0f172a'
                        }}
                    >
                        <Box sx={{ gridColumn: 'span 6', textAlign: 'right', pr: 2 }}>
                            TOTAL TTC (FCFA) :
                        </Box>
                        <Box sx={{ textAlign: 'right', fontWeight: 900 }}>{formatCurrency(totalTTC)}</Box>
                        <Box sx={{ gridColumn: 'span 2' }}></Box>
                    </Box>
                </Box>

                {/* ACTIONS EFFECTUÉES PAR LE SYSTÈME */}
                <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1, color: '#0f172a' }}>
                    Actions effectuées par le système lors de la validation
                </Typography>
                <Box sx={{ border: '1px solid #cbd5e1', borderRadius: '4px', p: 1.5, mb: 3, bgcolor: '#f8fafc' }}>
                    {[
                        'Génération définitive des écritures comptables',
                        `Création du fichier d'interface SAGE (${nomFichierSage})`,
                        'Marquage des factures comme « Transférées »',
                        'Mémorisation de la date, de l\'heure, de l\'utilisateur et du numéro de transfert'
                    ].map((act, idx) => (
                        <Box key={idx} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.5, borderBottom: idx < 3 ? '1px dashed #e2e8f0' : 'none' }}>
                            <Typography variant="caption">• {act}</Typography>
                            <Typography variant="caption" sx={{ color: '#16a34a', fontWeight: 'bold' }}>EFFECTUÉ</Typography>
                        </Box>
                    ))}
                </Box>

                {/* ZONE DE VISAS */}
                <Box sx={{ border: '1px solid #cbd5e1', borderRadius: '4px', p: 1.5, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                    <Box>
                        <Typography variant="caption" display="block">
                            <strong>Validé par (le comptable) :</strong> {user}
                        </Typography>
                        <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                            <strong>Visa / Date :</strong> _____________________________
                        </Typography>
                    </Box>
                    <Box>
                        <Typography variant="caption" display="block">
                            <strong>Visa Contrôle (le cas échéant) :</strong>
                        </Typography>
                        <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                            _________________________________________
                        </Typography>
                    </Box>
                </Box>
            </Paper>
        </Box>
    );
};

export default BordereauValidationViewer;
